import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Event } from '../types/index.ts';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { QrCode, Camera, CheckCircle, XCircle, ArrowLeft, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';

export default function TicketScanner() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanHistory, setScanHistory] = useState<any[]>([]);
  const [stats, setStats] = useState({ total_scanned: 0, total_tickets: 0 });
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [lastScanResult, setLastScanResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (eventId) {
      loadEventData();
      loadScanHistory();
    }
    return () => {
      stopScanner();
    };
  }, [eventId]);

  const loadEventData = async () => {
    try {
      const data = await api.getEvent(eventId!);
      setEvent(data);
      const scanStats = await api.getScanStats(eventId!);
      setStats(scanStats);
    } catch (error) {
      toast.error('Failed to load event details');
    }
  };

  const loadScanHistory = async () => {
    try {
      const history = await api.getScanHistory(eventId!);
      setScanHistory(history);
    } catch (error) {
      console.error('Failed to load scan history:', error);
    }
  };

  const startScanner = async () => {
    try {
      setIsScanning(true);
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        onScanSuccess,
        () => {} // onScanError - ignore errors
      );
    } catch (error: any) {
      toast.error('Failed to start camera. Please allow camera access.');
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
    }
    setIsScanning(false);
  };

  const onScanSuccess = async (decodedText: string) => {
    // Prevent multiple scans
    if (!scannerRef.current) return;

    try {
      // Stop scanner temporarily
      await stopScanner();

      // Scan the ticket
      const result = await api.scanTicket(decodedText);
      
      setLastScanResult({ success: true, message: 'Ticket verified successfully!' });
      toast.success('Ticket verified!');
      
      // Refresh stats and history
      await loadEventData();
      await loadScanHistory();

      // Auto-restart scanner after 2 seconds
      setTimeout(() => {
        setLastScanResult(null);
        startScanner();
      }, 2000);
    } catch (error: any) {
      setLastScanResult({ success: false, message: error.message || 'Invalid ticket' });
      toast.error(error.message || 'Invalid ticket');
      
      // Auto-restart scanner after 2 seconds
      setTimeout(() => {
        setLastScanResult(null);
        startScanner();
      }, 2000);
    }
  };

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/manage-events')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">{event.title}</h1>
          <p className="text-gray-400">Ticket Scanner</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass border-white/10">
          <CardContent className="p-4 text-center">
            <Zap className="h-8 w-8 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total_scanned}</p>
            <p className="text-sm text-gray-400">Scanned</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardContent className="p-4 text-center">
            <QrCode className="h-8 w-8 text-secondary mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total_tickets}</p>
            <p className="text-sm text-gray-400">Total Tickets</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">
              {stats.total_tickets > 0 ? ((stats.total_scanned / stats.total_tickets) * 100).toFixed(0) : 0}%
            </p>
            <p className="text-sm text-gray-400">Check-in Rate</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardContent className="p-4 text-center">
            <XCircle className="h-8 w-8 text-gray-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total_tickets - stats.total_scanned}</p>
            <p className="text-sm text-gray-400">Remaining</p>
          </CardContent>
        </Card>
      </div>

      {/* Scanner */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>QR Code Scanner</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isScanning ? (
            <div className="text-center py-12">
              <Camera className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-6">Ready to scan tickets</p>
              <Button onClick={startScanner} className="shadow-glow">
                <Camera className="h-4 w-4 mr-2" />
                Start Scanner
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div id="qr-reader" className="rounded-lg overflow-hidden"></div>
              
              {lastScanResult && (
                <Card className={`border-2 ${
                  lastScanResult.success
                    ? 'bg-green-500/10 border-green-500'
                    : 'bg-destructive/10 border-destructive'
                }`}>
                  <CardContent className="p-4 flex items-center space-x-3">
                    {lastScanResult.success ? (
                      <CheckCircle className="h-6 w-6 text-green-500" />
                    ) : (
                      <XCircle className="h-6 w-6 text-destructive" />
                    )}
                    <p className="font-medium">{lastScanResult.message}</p>
                  </CardContent>
                </Card>
              )}
              
              <Button onClick={stopScanner} variant="outline" className="w-full glass-hover">
                Stop Scanner
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scan History */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Recent Scans</CardTitle>
        </CardHeader>
        <CardContent>
          {scanHistory.length > 0 ? (
            <div className="space-y-2">
              {scanHistory.slice(0, 10).map((scan, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg glass border-white/10"
                >
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium">{scan.user_name || 'Unknown User'}</p>
                      <p className="text-xs text-gray-400">{scan.ticket_type}</p>
                    </div>
                  </div>
                  <Badge variant="secondary">{scan.time}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">No scans yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
